<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">站内信管理</h1>
      <button @click="handleCreate"
        class="primary-btn flex items-center gap-2">
        <PlusIcon class="w-5 h-5" />
        发送新消息
      </button>
    </div>

    <!-- 筛选器 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div class="flex flex-wrap gap-4">
        <!-- 优先级筛选 -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">优先级:</label>
          <select v-model="filterForm.priority" @change="handleFilter"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option :value="undefined">全部</option>
            <option :value="MessagePriority.HIGH">高优先级</option>
            <option :value="MessagePriority.NORMAL">普通</option>
            <option :value="MessagePriority.LOW">低优先级</option>
          </select>
        </div>

        <!-- 搜索框 -->
        <div class="flex-1 flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">搜索:</label>
          <div class="flex-1 max-w-md flex">
            <input v-model="filterForm.search" @keyup.enter="handleFilter" type="text" placeholder="搜索标题或内容"
              class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none" />
            <button @click="handleFilter"
              class="primary-btn rounded-l-none rounded-r-md">
              <MagnifyingGlassIcon class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 消息列表 -->
    <div v-else-if="messages.length > 0" class="space-y-4">
      <MessageItem v-for="message in messages" :key="message.id" :message="message" @view="handleView"
        @edit="handleEdit" @delete="handleDelete" />
    </div>

    <!-- 空状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <div class="text-gray-400 dark:text-gray-500 mb-4">
        <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">暂无消息</p>
      <p class="text-sm text-gray-500 dark:text-gray-400">点击右上角"发送新消息"按钮创建第一条消息</p>
    </div>

    <!-- 分页 -->
    <Pagination v-if="pagination.total > 0"
      :total="pagination.total"
      v-model:current-page="pagination.page"
      v-model:page-size="pagination.limit"
      :show-jumper="false"
      @update:current-page="loadMessages"
      @update:page-size="loadMessages"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import MessageItem from '@/components/message/MessageItem.vue'
import Pagination from '@/components/ui/Pagination.vue'
import { getMessagesAPI, deleteMessageAPI } from '@/api/message'
import { MessagePriority } from '@/types/message'
import type { Message, MessageListParams, Pagination as PaginationType } from '@/types/message'
import toaster from '@/utils/toaster'

const router = useRouter()

/**
 * 状态
 */
const loading = ref(false)
const messages = ref<Message[]>([])
const pagination = reactive<PaginationType>({
  page: 1,
  limit: 20,
  total: 0,
})

/**
 * 筛选表单
 */
const filterForm = reactive<MessageListParams>({
  priority: undefined,
  search: '',
})

/**
 * 加载消息列表
 */
const loadMessages = async () => {
  loading.value = true
  try {
    const params: MessageListParams = {
      page: pagination.page,
      limit: pagination.limit,
      priority: filterForm.priority,
      search: filterForm.search || undefined,
    }

    const response = await getMessagesAPI(params)

    if (response.status === 200 && response.data) {
      // API返回的是messages字段
      messages.value = response.data.messages || []
      Object.assign(pagination, response.data.pagination)
    }
  } catch (error) {
    toaster.error('加载消息列表失败')
    console.error('Load messages error:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 筛选
 */
const handleFilter = () => {
  pagination.page = 1
  loadMessages()
}

/**
 * 创建新公告
 */
const handleCreate = () => {
  router.push('/message/create')
}

/**
 * 查看详情
 */
const handleView = (messageId: number) => {
  router.push(`/message/${messageId}`)
}

/**
 * 编辑
 */
const handleEdit = (messageId: number) => {
  router.push(`/message/${messageId}/edit`)
}

/**
 * 删除
 */
const handleDelete = async (messageId: number) => {
  if (!confirm('删除后将无法恢复，确定要删除这条公告吗？')) {
    return
  }

  try {
    const response = await deleteMessageAPI(messageId)

    if (response.status === 200) {
      toaster.success('删除成功')
      loadMessages()
    }
  } catch (error) {
    toaster.error('删除失败')
    console.error('Delete message error:', error)
  }
}

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadMessages()
})
</script>
